import { motion } from "framer-motion";
import { Children, isValidElement } from "react";

export function Surface({ className = "", children, testid, ...p }) {
  return (
    <div
      className={`rounded-2xl border ios-shadow ${className}`}
      style={{ background: "var(--surface)", borderColor: "var(--border)" }}
      data-testid={testid}
      {...p}
    >
      {children}
    </div>
  );
}

export function LargeTitle({ children, subtitle, right }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
          {children}
        </h1>
        {subtitle && <p className="text-sm mt-1 text-secondary">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, tone = "default", hint, testid }) {
  const toneColor = {
    default: "var(--accent)",
    success: "var(--success)",
    warning: "var(--warning)",
    danger: "var(--danger)",
  }[tone];
  return (
    <Surface className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-secondary">{label}</span>
        {Icon && (
          <div
            className="h-8 w-8 rounded-full grid place-items-center"
            style={{ background: "var(--accent-soft)" }}
          >
            <Icon size={16} style={{ color: toneColor }} strokeWidth={2} />
          </div>
        )}
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
        {value}
      </div>
      {hint && <div className="text-xs mt-1 text-secondary">{hint}</div>}
    </Surface>
  );
}

export function ListGroup({ header, children, className = "" }) {
  return (
    <div className={className}>
      {header && (
        <div className="px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-secondary">
          {header}
        </div>
      )}
      <Surface className="overflow-hidden">
        <div className="divide-y" style={{ borderColor: "var(--border)" }}>
          {children}
        </div>
      </Surface>
    </div>
  );
}

export function ListItem({ children, onClick, right, className = "", testid }) {
  return (
    <div
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center justify-between gap-3 px-4 py-3.5 ${
        onClick ? "cursor-pointer active:opacity-60 transition-opacity" : ""
      } ${className}`}
      style={{ borderColor: "var(--border)" }}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {right}
    </div>
  );
}

export function Segmented({ options, value, onChange, testid }) {
  return (
    <div
      className="inline-flex p-1 rounded-full"
      style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
      data-testid={testid}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            data-testid={`segment-${o.value}`}
            onClick={() => onChange(o.value)}
            className="relative px-4 py-1.5 text-sm font-medium rounded-full transition-colors"
            style={{ color: active ? "var(--accent-fg)" : "var(--text-secondary)" }}
          >
            {active && (
              <motion.div
                layoutId={testid || "seg"}
                className="absolute inset-0 rounded-full accent-bg"
                transition={{ type: "spring", stiffness: 400, damping: 32 }}
              />
            )}
            <span className="relative z-10">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

const TONES = {
  success: "var(--success)",
  warning: "var(--warning)",
  danger: "var(--danger)",
  accent: "var(--accent)",
  neutral: "var(--text-secondary)",
};

export function Pill({ tone = "neutral", children, className = "", dot, testid, ...rest }) {
  const c = TONES[tone] || TONES.neutral;
  const kids = Children.toArray(children);
  const hasElement = kids.some(isValidElement); // icon present → skip the dot
  const showDot = dot ?? !hasElement;
  return (
    <span
      {...rest}
      data-testid={testid || rest["data-testid"]}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold leading-none border [&_svg]:shrink-0 ${className}`}
      style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: hasElement ? c : "var(--text)" }}
    >
      {showDot && <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: c }} />}
      {children}
    </span>
  );
}

export function AButton({ children, onClick, variant = "filled", tone = "accent", className = "", disabled, testid, type = "button" }) {
  const bg = TONES[tone] || TONES.accent;
  if (variant === "text")
    return (
      <button
        type={type}
        data-testid={testid}
        onClick={onClick}
        disabled={disabled}
        className={`font-semibold text-sm transition-opacity active:opacity-50 disabled:opacity-40 ${className}`}
        style={{ color: bg }}
      >
        {children}
      </button>
    );
  return (
    <motion.button
      type={type}
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      whileTap={{ scale: 0.96 }}
      className={`font-semibold text-sm rounded-full px-5 py-2.5 transition-opacity disabled:opacity-40 ${className}`}
      style={{ background: bg, color: tone === "accent" ? "var(--accent-fg)" : "#fff" }}
    >
      {children}
    </motion.button>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-20 px-6">
      {Icon && (
        <div className="h-16 w-16 rounded-2xl grid place-items-center mb-4" style={{ background: "var(--accent-soft)" }}>
          <Icon size={28} className="accent-text" strokeWidth={1.75} />
        </div>
      )}
      <div className="text-lg font-semibold" style={{ color: "var(--text)" }}>{title}</div>
      {subtitle && <div className="text-sm text-secondary mt-1 max-w-xs">{subtitle}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`shimmer rounded-xl ${className}`} />;
}

export function SearchInput({ value, onChange, placeholder = "Search…", testid }) {
  return (
    <div className="relative flex-1 min-w-[180px]">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="15" height="15" viewBox="0 0 24 24"
        fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
      </svg>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm rounded-full pl-9 pr-3 py-2 outline-none border transition-colors focus:border-[var(--accent)]"
        style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}
      />
    </div>
  );
}

export function ConditionDots({ condition }) {
  const map = [
    ["clean", "Clean"],
    ["undamaged", "Undamaged"],
    ["working", "Working"],
  ];
  return (
    <div className="flex gap-1.5">
      {map.map(([k, label]) => {
        if (condition[k] === undefined || condition[k] === null) return null;
        const ok = condition[k] === true;
        return (
          <span
            key={k}
            title={label}
            className="h-2.5 w-2.5 rounded-full"
            style={{ background: ok ? "var(--success)" : "var(--danger)" }}
          />
        );
      })}
    </div>
  );
}
